import React from 'react';
import { Icon, Label } from 'semantic-ui-react';
import { ContractDocusignSendHistoryDto } from '../../services/contracts/ContractService';
import {
  EDocusignSendHistoryStatus,
  formatDocusignDateTime,
  getDocusignStatusLabel,
  normalizeDocusignSendStatus,
} from '../../utils/docusignSendHistoryUtils';

const POPOVER_ICON = 'docusign-history-flat-icon';

function entryHeaderIcon(
  status: number | string,
  isProcessing: boolean,
  isLatest: boolean
): { name: 'check circle' | 'warning circle' | 'arrow right'; color: 'green' | 'red' | 'grey' } | null {
  if (!isLatest) {
    return { name: 'arrow right', color: 'grey' };
  }
  const code = normalizeDocusignSendStatus(status, isProcessing);
  if (code === EDocusignSendHistoryStatus.Processing) return null;
  if (code === EDocusignSendHistoryStatus.Success) return { name: 'check circle', color: 'green' };
  return { name: 'warning circle', color: 'red' };
}

function statusLabelColor(
  status: number | string,
  isProcessing: boolean
): 'blue' | 'green' | 'red' {
  const code = normalizeDocusignSendStatus(status, isProcessing);
  if (code === EDocusignSendHistoryStatus.Processing) return 'blue';
  if (code === EDocusignSendHistoryStatus.Success) return 'green';
  return 'red';
}

export interface DocusignHistoryEntryProps {
  item: ContractDocusignSendHistoryDto;
  index: number;
  isLast: boolean;
}

const DocusignHistoryEntry: React.FC<DocusignHistoryEntryProps> = ({
  item,
  index,
  isLast,
}) => {
  const headerIcon = entryHeaderIcon(item.status, item.isProcessing, index === 0);
  const detailStyle: React.CSSProperties = {
    fontSize: '0.85em',
    color: '#666',
    marginLeft: '24px',
  };
  const lineStyle: React.CSSProperties = { marginBottom: '2px' };

  return (
    <div
      style={{
        marginBottom: isLast ? 0 : '14px',
        paddingBottom: isLast ? 0 : '12px',
        borderBottom: isLast ? 'none' : '1px solid #eee',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px', flexWrap: 'wrap' }}>
        {headerIcon && (
          <Icon name={headerIcon.name} color={headerIcon.color} className={POPOVER_ICON} />
        )}
        <strong style={{ fontSize: '0.95em' }}>
          {getDocusignStatusLabel(item.status, item.isProcessing)}
        </strong>
        {index === 0 && (
          <Label size="mini" color={statusLabelColor(item.status, item.isProcessing)}>
            Último
          </Label>
        )}
      </div>

      <div style={detailStyle}>
        <div style={lineStyle}>
          <Icon name="user" size="small" className={POPOVER_ICON} />
          <span style={{ marginLeft: '4px' }}>Solicitante: {item.requestedByUserName || '—'}</span>
        </div>
        <div style={lineStyle}>
          <Icon name="calendar alternate" size="small" className={POPOVER_ICON} />
          <span style={{ marginLeft: '4px' }}>Solicitado: {formatDocusignDateTime(item.requestedAt)}</span>
        </div>
        {item.finishedAt && (
          <div style={lineStyle}>
            <Icon name="calendar check" size="small" className={POPOVER_ICON} />
            <span style={{ marginLeft: '4px' }}>Finalizado: {formatDocusignDateTime(item.finishedAt)}</span>
          </div>
        )}
        <div style={lineStyle}>
          <Icon name="address card" size="small" className={POPOVER_ICON} />
          <span style={{ marginLeft: '4px' }}>
            Destinatário ({item.recipientContactTypeText}): {item.recipientName}
          </span>
        </div>
        <div style={lineStyle}>
          <Icon name="phone" size="small" className={POPOVER_ICON} />
          <span style={{ marginLeft: '4px' }}>Telefone: {item.recipientPhone || '—'}</span>
        </div>
        {item.recipientEmail && (
          <div style={lineStyle}>
            <Icon name="mail" size="small" className={POPOVER_ICON} />
            <span style={{ marginLeft: '4px' }}>E-mail: {item.recipientEmail}</span>
          </div>
        )}
        {item.docusignAccountEmail && (
          <div style={lineStyle}>
            <Icon name="id badge" size="small" className={POPOVER_ICON} />
            <span style={{ marginLeft: '4px' }}>Conta DocuSign: {item.docusignAccountEmail}</span>
          </div>
        )}
        {item.docusignContractLink && (
          <div style={{ marginTop: '6px' }}>
            <Icon name="external alternate" size="small" className={POPOVER_ICON} />
            <a href={item.docusignContractLink} target="_blank" rel="noreferrer" style={{ marginLeft: '4px' }}>
              Abrir no DocuSign
            </a>
          </div>
        )}
        {item.errorMessage && (
          <div style={{ fontSize: '0.9em', color: '#c0392b', fontStyle: 'italic', marginTop: '4px' }}>
            <Icon name="warning circle" size="small" className={POPOVER_ICON} />
            <span style={{ marginLeft: '4px' }}>{item.errorMessage}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default DocusignHistoryEntry;
